import React from 'react';
import styled from 'styled-components';
import { useLightbox } from 'simple-react-lightbox';
import Slider from 'react-slick';
import PropTypes from 'prop-types';

import $ from '../../styles/global';
import RightChevronIcon from '../../assets/icons/right-chevron.svg';
import LeftChevronIcon from '../../assets/icons/left-chevron.svg';
import Lightbox from './Lightbox';

const Container = styled.div``;

const InnerContainer = styled.div`
  width: 95%;
  margin: 0 auto 134px auto;

  .slick-prev,
  .slick-next {
    font-size: 0;
    line-height: 0;
    position: absolute;
    top: 50%;
    display: block;
    width: 20px;
    height: 20px;
    padding: 0;
    -webkit-transform: translate(0, -50%);
    -ms-transform: translate(0, -50%);
    transform: translate(0, -50%);
    cursor: pointer;
    color: transparent;
    border: none;
    outline: none;
    background: transparent;
  }

  .slick-prev {
    left: -25px;
  }

  .slick-next {
    right: -25px;
  }

  .slick-dots {
    position: absolute;
    bottom: -25px;
    display: block;
    width: 100%;
    padding: 0;
    margin: 0;
    list-style: none;
    text-align: center;

    li {
      position: relative;
      display: inline-block;
      width: 20px;
      height: 20px;
      margin: 0 5px;
      padding: 0;
      cursor: pointer;
      button {
        font-size: 0;
        line-height: 0;
        display: block;
        width: 20px;
        height: 20px;
        padding: 5px;
        cursor: pointer;
        color: transparent;
        border: 0;
        outline: none;
        background: transparent;
        &:hover,
        &:focus {
          outline: none;
        }
      }
    }
  }

  .slick-dotted.slick-slider {
    margin-bottom: 30px;
  }

  .slick-list {
    // ensures that the box shadow from the cards are not clipped
    height: 285px;
  }

  .slick-next,
  .slick-prev {
    width: 34px;
    height: 34px;
    fill: ${$.color.base};
  }

  .slick-dots {
    li {
      width: 10px;
      height: 10px;
      margin: 0 ${$.layout().margin2}px;

      &.slick-active {
        button {
          background-color: ${$.color.red};
        }
      }

      button {
        width: 100%;
        height: 100%;
        padding: 0;
        border-radius: 99px;
        background-color: ${$.color.gray1};
        &:before {
          display: none;
        }
      }
    }
  }

  ${$.device.mobile} {
    .slick-dots {
      display: none !important;
    }
  }
`;

const Title = styled.div`
  color: ${$.color.base};
  font-size: 30px;
  font-family: Lato Regular;
  margin-bottom: ${$.layout().margin4}px;
`;

const Thumbnail = styled.div`
  ${({ length }) => `
  & > * {
    margin: 0 ${$.layout().margin2}px;
    border: 1px solid ${$.color.base};
    border-radius: 10px;
    box-shadow: ${$.dropShadow.normal};
    width: calc(${length === 1 ? '50%' : '100%'} - ${$.layout().margin2 * 2}px);
    height: calc(100% - 2px);
  }

  img {
    object-fit: contain;
  }

  &:hover {
    cursor: pointer;
  }

  ${$.device.desktop} {
    width: calc(275px - 2px);
    height: calc(275px - 2px);
  }

  ${$.device.tablet} {
    width: calc(275px - 2px);
    height: calc(275px - 2px);

    & > * {
      width: calc(${length === 1 ? '50%' : '100%'} - ${
    $.layout().margin2 * 2
  }px); 
    }
  }

  ${$.device.mobile} {
    width: 100%;
    height: 275px;

    & > * {
      width: calc(100% - ${$.layout().margin2 * 2}px);
    }
  }
`}
`;

const StyledNextArrow = styled(({ className, children, onClick, style }) => (
  <RightChevronIcon
    className={className}
    style={{ ...style }}
    onClick={onClick}
  >
    {children}
  </RightChevronIcon>
))``;

const StyledPrevArrow = styled(({ className, children, onClick, style }) => (
  <LeftChevronIcon className={className} style={{ ...style }} onClick={onClick}>
    {children}
  </LeftChevronIcon>
))``;

const CarouselLightbox = ({ title, images, slidesToShow }) => {
  const { openLightbox } = useLightbox();

  return (
    <Container>
      <Title>{title}</Title>
      <InnerContainer>
        <Slider
          dots
          infinite
          speed={500}
          slidesToShow={slidesToShow}
          slidesToScroll={1}
          nextArrow={<StyledNextArrow />}
          prevArrow={<StyledPrevArrow />}
          responsive={[
            {
              breakpoint: 1199,
              settings: {
                slidesToShow: images.length > 1 ? 2 : 1,
                slidesToScroll: 1,
              },
            },
            {
              breakpoint: 767,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
              },
            },
          ]}
        >
          {images.map(({ key, thumbnail }, index) => (
            <Thumbnail
              key={key}
              length={images.length}
              onClick={() => {
                openLightbox(index);
              }}
            >
              <img src={thumbnail} alt="" />
            </Thumbnail>
          ))}
        </Slider>
        <Lightbox images={images} />
      </InnerContainer>
    </Container>
  );
};

CarouselLightbox.defaultProps = {
  slidesToShow: 2,
  title: 'Structural Drawings',
};

CarouselLightbox.propTypes = {
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  slidesToShow: PropTypes.number,
  title: PropTypes.string,
};

export default CarouselLightbox;
